import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { BonusGroup, BonusValidityEntry } from '../../types/free-bonus'
import { api } from '../../utils/api-client'
import { queryKeys } from './keys'

const TODAY = 'today'
const YESTERDAY = 'yesterday'

/**
 * Collapse the flat validity list into one entry per `bonus_name`.
 *
 * `today`/`yesterday` drive the badge counts; any other (or missing) `type`
 * stays in the group but is excluded from both counts. Label/icon and the
 * claim target follow the same priority: yesterday → today → any item.
 */
export function groupBonusesByName(bonuses: BonusValidityEntry[]): BonusGroup[] {
  const groups = new Map<string, BonusValidityEntry[]>()

  for (const bonus of bonuses) {
    const existing = groups.get(bonus.bonus_name)
    if (existing) existing.push(bonus)
    else groups.set(bonus.bonus_name, [bonus])
  }

  return Array.from(groups, ([bonusName, items]) => {
    const todayItems = items.filter(item => item.type === TODAY)
    const yesterdayItems = items.filter(item => item.type === YESTERDAY)

    const primary = yesterdayItems[0] ?? todayItems[0] ?? items[0]

    return {
      bonusName,
      icon: primary.icon,
      label: primary.label ?? bonusName,
      todayCount: todayItems.length,
      yesterdayCount: yesterdayItems.length,
      claimId: primary.id,
    }
  })
}

export function useAvailableBonuses(options?: { enabled?: boolean }) {
  const username = useBoundStore(s => s.user.username)

  const { data, isFetching } = useQuery<BonusValidityEntry[]>({
    queryKey: [...queryKeys.available_bonuses, username],
    queryFn: async ({ signal }) => {
      const res = await api.post<ApiResponse<BonusValidityEntry[]>>(
        '/ajax_data.php',
        { type: 'check_bonus_validity' },
        { signal },
      )
      return res.data.status === 1 ? res.data.info : []
    },
    enabled: Boolean(username) && options?.enabled !== false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  })

  const availableBonuses = data ?? []
  const groupedBonuses = useMemo(
    () => groupBonusesByName(availableBonuses),
    [availableBonuses],
  )

  return {
    availableBonuses,
    groupedBonuses,
    isLoading: isFetching,
    badgeCount: groupedBonuses.length,
  }
}
